import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { ReleaseCard } from '@components/SongCards/ReleasesSongCard';
import { CarouselWithHeader } from '@components/main-components/carousel/HeaderCarousel';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, useInfiniteQuery } from '@graphql/reactQuery';
import { ArtistAppearsOnCarouselDocument, ArtistAppearsOnFragmentDoc } from '@graphql/reactQuery';
import { formatProfileHeader } from '@utils/helpers';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment ArtistAppearsOn on Artist {
    id
    appearsOnNumReleases: numReleases(
      filter: {
        releaseAuthor: ONLY_APPEARS_ON
        creditSplit: ALL
        releaseAlbumRevealStatus: ONLY_NOT_REVEALED_ALBUMS
      }
    )
    user {
      id
      ...CarouselUserInfo
    }
  }

  query ArtistAppearsOnCarousel($artistId: UUID!, $after: String) {
    artist(id: $artistId) {
      id
      appearsOnReleases: releases(
        pagination: { first: 10, after: $after }
        filter: {
          releaseAuthor: ONLY_APPEARS_ON
          creditSplit: ALL
          releaseAlbumRevealStatus: ONLY_NOT_REVEALED_ALBUMS
        }
      ) {
        edges {
          node {
            id
            ...ReleasesCard
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`);

export const ArtistAppearsOnCarousel = ({
  artistFrag,
}: {
  artistFrag: FragmentType<ArtistAppearsOnFragmentDoc>;
}) => {
  const artist = getFragment(ArtistAppearsOnFragmentDoc, artistFrag);

  const artistId = artist.id;

  const { orderedList: mintedReleases, loadMoreNextPage: loadMore } = useInfiniteQuery(
    ArtistAppearsOnCarouselDocument,
    {
      filterQueryKey: {
        artistId,
      },
      variables({ pageParam }) {
        return {
          artistId,
          after: pageParam?.after || null,
        };
      },
      getNextPageParam({ data }) {
        return (
          data.artist?.appearsOnReleases.pageInfo.hasNextPage && {
            after: data.artist.appearsOnReleases.pageInfo.endCursor,
          }
        );
      },
      list({ artist }) {
        return artist?.appearsOnReleases.edges.map(edge => edge.node);
      },
      uniq({ id }) {
        return id;
      },
      staleTime: 0,
    },
  );

  const mintedReleaseSlides = React.useMemo(() => {
    return mintedReleases?.map(release => ({
      id: release.id,
      slide: (
        <ReleaseCard
          data={release}
          playlist={{
            type: 'ARTIST',
            associationId: artist.id,
          }}
        />
      ),
    }));
  }, [mintedReleases, artist.id]);

  if (!mintedReleaseSlides || mintedReleaseSlides.length === 0) {
    return null;
  }

  const subheader = formatProfileHeader({ count: artist.appearsOnNumReleases, text: 'Releases' });

  return (
    <CarouselWithHeader
      user={artist.user}
      header="Appears On"
      subheader={subheader}
      slides={mintedReleaseSlides}
      path="/appears-on"
      loadMore={loadMore}
      seeAllParams={null}
    />
  );
};
